// src/components/OutreachPanel.jsx
// Voice notes for every note box (contact log, dealer notes, sales plans):
// useVoiceNote + VoiceRecordControls + the recording timer. The old Outreach
// composer was merged into the contact log (customer360/ContactLogPanel).
import React, { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import { apiFetch } from "../apiFetch";
import { fmtDateTimeLA } from "../utils/dates";
import {
  VOICE_CHANGED_EVENT, audioExt, deleteRec, getRecBlob, listRecs, notifyVoiceRecsChanged,
} from "../utils/voiceStore";
import {
  busyIds, getRecorderState, memoryRecs, registerOwner, startRecording, stopRecording, subscribeRecorder,
} from "../utils/voiceRecorder";

// m:ss counter shown on the Stop button while recording. Counts from the
// recording's start so it stays right after switching screens and back. Owns
// its own 1s timer so the tick re-renders only this span.
export function RecordingElapsed({ startedAt }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const secs = Math.max(0, Math.floor((now - (startedAt || now)) / 1000));
  return <>{Math.floor(secs / 60)}:{String(secs % 60).padStart(2, "0")}</>;
}

// Whisper's upload cap; the server enforces the same limit.
const MAX_AUDIO_BYTES = 25 * 1024 * 1024;
// A recording another tab is still writing has touched its record within this
// window; older unfinished ones were cut off (tab closed / crashed) and are
// offered for recovery.
const LIVE_RECORDING_MS = 15000;

function fmtDuration(ms) {
  const secs = Math.max(0, Math.round((ms || 0) / 1000));
  return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`;
}

function downloadBlob(blob, rec) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const d = new Date(rec.startedAt || Date.now());
  const p2 = (n) => String(n).padStart(2, "0");
  a.href = url;
  a.download = `voice-note-${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}_${p2(d.getHours())}${p2(d.getMinutes())}.${audioExt(rec.mimeType)}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// Mic → /api/admin/transcribe-audio → onText("🎙️ Voice Note …summary + transcript").
// The mic itself lives in utils/voiceRecorder (one per app), so switching
// screens mid-recording doesn't stop it. Audio is saved to IndexedDB as it
// records and kept until it transcribes, so a failed upload leaves a
// Retry / Download / Discard row instead of losing it.
export function useVoiceNote(onText) {
  const [transcribing, setTranscribing] = useState(false);
  const [pending, setPending] = useState([]);
  const ownerIdRef = useRef(null);
  if (!ownerIdRef.current) ownerIdRef.current = Math.random().toString(36).slice(2);
  const onTextRef = useRef(onText);
  onTextRef.current = onText;
  const rs = useSyncExternalStore(subscribeRecorder, getRecorderState);
  // This panel shows Stop when it started the recording, or when the panel
  // that did has closed (Stop here then transcribes into this panel).
  const recording = !!rs.rec && (rs.ownerId === ownerIdRef.current || !rs.ownerMounted);
  const recordingElsewhere = !!rs.rec && !recording;

  const refresh = useCallback(async () => {
    const now = Date.now();
    const liveId = getRecorderState().rec?.id;
    const stored = (await listRecs()).filter((r) =>
      !busyIds.has(r.id) &&
      r.id !== liveId &&
      (r.endedAt || now - (r.updatedAt || r.startedAt || 0) > LIVE_RECORDING_MS)
    );
    const ids = new Set(stored.map((r) => r.id));
    const mem = [...memoryRecs.values()]
      .filter((m) => !ids.has(m.rec.id) && !busyIds.has(m.rec.id))
      .map((m) => m.rec);
    setPending([...stored, ...mem]);
  }, []);

  useEffect(() => {
    refresh();
    window.addEventListener(VOICE_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(VOICE_CHANGED_EVENT, refresh);
  }, [refresh]);

  const discard = async (rec) => {
    memoryRecs.delete(rec.id);
    await deleteRec(rec.id);
    refresh();
  };

  const transcribe = async (rec, blob) => {
    if (!blob || blob.size === 0) { toast.error("No audio recorded."); await discard(rec); return; }
    if (blob.size > MAX_AUDIO_BYTES) {
      toast.error("Recording is too large to transcribe (over 25 MB). Use Download to keep the audio.");
      notifyVoiceRecsChanged();
      return;
    }
    busyIds.add(rec.id);
    notifyVoiceRecsChanged();
    setTranscribing(true);
    try {
      const fd = new FormData();
      fd.append("audio", blob, `recording.${audioExt(blob.type || rec.mimeType)}`);
      const mb = blob.size / 1048576;
      const res = await apiFetch("/api/admin/transcribe-audio", {
        method: "POST",
        body: fd,
        timeoutMs: Math.min(300000, 90000 + Math.round(mb * 20000)),
      });
      if (!res?.ok) throw new Error(res?.error || "Transcription failed");
      onTextRef.current?.(`🎙️ Voice Note\n\n${res.summary}\n\n--- Transcript ---\n${res.transcript}`);
      memoryRecs.delete(rec.id);
      await deleteRec(rec.id);
    } catch (e) {
      const msg = e?.message === "SESSION_EXPIRED" ? "Session expired — sign in again" : (e?.message || "Failed to transcribe audio");
      toast.error(`${msg}. Your recording was kept — use Retry or Download.`);
    } finally {
      busyIds.delete(rec.id);
      setTranscribing(false);
      notifyVoiceRecsChanged();
    }
  };
  const transcribeRef = useRef(transcribe);
  transcribeRef.current = transcribe;

  // Leaving the screen does not stop the recording; it only stops this panel
  // from receiving the transcript.
  useEffect(() => registerOwner(ownerIdRef.current, (rec, blob) => transcribeRef.current(rec, blob)), []);

  const start = async () => {
    try {
      await startRecording(ownerIdRef.current);
    } catch {
      toast.error("Microphone access denied or not available.");
    }
  };

  const stop = () => stopRecording(ownerIdRef.current);

  const blobFor = async (rec) => memoryRecs.get(rec.id)?.blob || (await getRecBlob(rec));

  const retry = async (rec) => {
    const blob = await blobFor(rec);
    if (!blob) { toast.error("Recording audio not found."); return; }
    await transcribe(rec, blob);
  };

  const download = async (rec) => {
    const blob = await blobFor(rec);
    if (!blob) { toast.error("Recording audio not found."); return; }
    downloadBlob(blob, rec);
  };

  return {
    recording, recordingElsewhere, startedAt: rs.rec?.startedAt,
    transcribing, start, stop, pending, retry, download, discard,
  };
}

export function VoiceRecordControls({ voice }) {
  const { recording, recordingElsewhere, startedAt, transcribing, start, stop, pending = [], retry, download, discard } = voice;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        {recording ? (
          <button
            type="button"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors"
            onClick={stop}
          >
            <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
            Stop (<RecordingElapsed startedAt={startedAt} />)
          </button>
        ) : (
          <button
            type="button"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 text-slate-600 hover:bg-slate-100 transition-colors"
            onClick={start}
            disabled={transcribing || recordingElsewhere}
            title={recordingElsewhere
              ? "Another note is recording — stop it first"
              : "Record a voice note — AI transcribes and summarizes it"}
          >
            <span className="w-2 h-2 rounded-full bg-red-500" />
            Record
          </button>
        )}
        {transcribing && (
          <span className="flex items-center gap-1 text-[11px] text-blue-600">
            <Loader2 className="w-3 h-3 animate-spin" /> Transcribing...
          </span>
        )}
      </div>
      {pending.map((rec) => (
        <div
          key={rec.id}
          className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-900"
        >
          <span>
            Untranscribed recording · {fmtDateTimeLA(new Date(rec.startedAt).toISOString())}
            {rec.endedAt ? ` · ${fmtDuration(rec.endedAt - rec.startedAt)}` : ""}
          </span>
          <span className="flex items-center gap-2">
            <button
              type="button"
              className="font-medium text-blue-700 hover:underline disabled:opacity-50"
              disabled={transcribing || recording}
              onClick={() => retry(rec)}
            >
              Retry
            </button>
            <button type="button" className="font-medium text-blue-700 hover:underline" onClick={() => download(rec)}>
              Download
            </button>
            <button
              type="button"
              className="font-medium text-slate-500 hover:text-red-600 hover:underline"
              onClick={() => { if (window.confirm("Discard this recording? The audio will be deleted.")) discard(rec); }}
            >
              Discard
            </button>
          </span>
        </div>
      ))}
    </div>
  );
}
